import adminRequest from '../../../config/adminRequest';
import { UsdtUri } from '../../core/configs/services';
import { AdminResponse } from '../models/adminResponse';
import { BoSignHelper } from '../../../core/providers/helper/boSign-helper';

export type OperationWallet = {
  id: string;
  channel: string;
  address: string;
  type: 'RENTAL' | 'PAYOUT';
  enabled: number;
  collectible: number;
  isDefault: number;
  note?: string;
  balance?: string;
  trxBalance?: string;
  balanceUpdatedAt?: string | null;
  /** 這筆運營錢包的登記時間。重生建的錢包與鏈上鑄造時間只差毫秒,面板當「開立時間」用。 */
  createdAt?: string | null;
  /** 指定的 RENTAL 來源;null/undefined = 用預設 RENTAL。 */
  rentalAddress?: string | null;
  rentalUpdatedAt?: string | null;
  rentalUpdatedBy?: string | null;
};

/** 出款錢包輪替游標(唯讀)。nextAddress 是「不做金額過濾時」下一筆會用的錢包。 */
export type PayoutCursorStatus = {
  counter: number | null;
  ringSize: number;
  nextIndex: number | null;
  nextAddress: string | null;
  ring: string[];
};

export type InternalTransferBody = {
  fromAddress: string;
  toAddress: string;
  /** 'amount' = 指定幣種與金額;'sweep' = 全部清空(先 USDT 再 TRX)。 */
  mode?: 'amount' | 'sweep';
  token?: 'USDT' | 'TRX';
  amount?: number;
  requestKey: string;
};

export type InternalTransferResult = {
  txId?: string;
  usdt?: { amount: number; txId: string };
  trx?: { amount: number; txId: string };
};

export type InternalTransferStatus =
  | { status: 'processing' }
  | { status: 'done'; result: InternalTransferResult }
  | { status: 'failed'; error: string }
  | { status: 'unknown' };

export class OperationWalletProvider {
  static async list(channel = 'TRC') {
    const res = await adminRequest.get<AdminResponse<OperationWallet[]>>(
      UsdtUri(`/admin/operation-wallet?channel=${channel}`),
    );
    return res.data;
  }

  /**
   * 出款錢包目前輪到哪一顆。唯讀 —— 後端用 Redis GET 不是 INCR,開面板不會推進游標。
   */
  static async payoutCursor(channel = 'TRC') {
    const res = await adminRequest.get<AdminResponse<PayoutCursorStatus>>(
      UsdtUri(`/admin/operation-wallet/payout-cursor?channel=${channel}`),
    );
    return res.data.data;
  }

  /**
   * 重生步驟1:鑄一顆新的 TRON 錢包(vc_wallet_v2),回新地址。
   * 只產金鑰,不會註冊成運營錢包 —— 那是步驟2(create)的事。
   */
  static async createVcWallet() {
    const res = await adminRequest.post<AdminResponse<{ address: string }>>(
      UsdtUri(`/admin/operation-wallet/vc-wallet`),
      // 非空 body:permission proxy 遇到空物件會卡 30 秒(見 setDefault 的註解)
      { mint: 1 },
    );
    return res.data;
  }

  /** 運營錢包之間手動內轉。requestKey 由呼叫端產生一次,避免連點兩下轉兩次。 */
  static async internalTransfer(body: InternalTransferBody) {
    const res = await adminRequest.post<
      AdminResponse<{ requestKey: string; status: string }>
    >(UsdtUri(`/admin/operation-wallet/internal-transfer`), body);
    return res.data;
  }

  /** 輪詢內轉結果。內轉是非同步的 —— 送出只是「受理」,不是「完成」。 */
  static async internalTransferStatus(requestKey: string) {
    const res = await adminRequest.get<AdminResponse<InternalTransferStatus>>(
      UsdtUri(`/admin/operation-wallet/internal-transfer/${requestKey}`),
    );
    return res.data;
  }

  /** 立刻查鏈更新所有運營錢包餘額(面板的「重新整理」按鈕)。 */
  static async refreshBalances() {
    const res = await adminRequest.post<AdminResponse<{ refreshed: number }>>(
      UsdtUri(`/admin/operation-wallet/refresh-balance`),
      // Non-empty for the same reason as setDefault — an empty body hangs the permission
      // proxy for 30s. cc's refreshBalances takes no @Body().
      { refresh: 1 },
    );
    return res.data;
  }

  static async create(body: { channel: string; address: string; type: string; enabled?: number; note?: string }) {
    const res = await adminRequest.post<AdminResponse<OperationWallet>>(
      UsdtUri(`/admin/operation-wallet`),
      body,
    );
    return res.data;
  }

  static async update(id: string, body: { enabled?: number; note?: string; collectible?: number }) {
    const res = await adminRequest.put<AdminResponse<OperationWallet>>(
      UsdtUri(`/admin/operation-wallet/${id}`),
      body,
    );
    return res.data;
  }

  static async setDefault(id: string) {
    const res = await adminRequest.put<AdminResponse<{ wallet: OperationWallet; replacedAddress: string | null }>>(
      UsdtUri(`/admin/operation-wallet/${id}/default`),
      // NOT `{}`. The permission proxy drops an empty body but still forwards the
      // `content-length: 2` header it came with, so cc's body-parser waits 30s for two
      // bytes that never arrive and the request dies with "request aborted" — this endpoint
      // has never once succeeded. cc's setDefault takes no @Body(); the field is ignored.
      { isDefault: 1 },
    );
    return res.data;
  }

  /**
   * 棄用一顆運營錢包 —— 取代原本的 remove()。後端不再提供硬刪除:紀錄會留著,
   * 只是停用 + 不可歸集,並進到棄用錢包清單。鏈上 USDT/TRX 都是 0 才會過。
   */
  static async abandon(id: string) {
    const res = await adminRequest.post<AdminResponse<unknown>>(
      UsdtUri(`/admin/operation-wallet/${id}/abandon`),
    );
    return res.data;
  }

  /**
   * 指定出款錢包的 RENTAL 來源。rentalAddress 為 null = 清成預設。需 2FA + boSign。
   * code 要先過 normalizeOtp —— 開頭是 0 的驗證碼被 Number() 吃掉會讓後端長度檢查失敗。
   *
   * rentalAddress 一定要明確傳 null(不能是 undefined):BoSignHelper.generate 用
   * lodash toPairs 會把 undefined 的 key 算進 sign,但 JSON.stringify 送出時會把該
   * key 整個移除,兩端 sign 就對不上,回 403 sign_invalidate(看起來像 2FA 壞掉,
   * 其實是簽章對不齊)。
   */
  static async setRentalSource(id: string, rentalAddress: string | null, code: string) {
    const data = { rentalAddress, code };
    const res = await adminRequest.post<AdminResponse<OperationWallet>>(
      UsdtUri(`/admin/operation-wallet/${id}/rental-source`),
      { ...data, boSign: BoSignHelper.generate(data) },
    );
    return res.data;
  }
}
